import { useEffect, useState, type JSX } from 'react';

import { cx } from '@emotion/css';
import { Button, Col, Space, Form } from 'antd';
import { Formik } from 'formik';
import { includes, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  confirmMfaCode,
  logout,
  resendMfaCode,
  doClearConfirmMfaCode,
  doClearResendMfaCode,
  setAuthentication,
} from 'src/actions';
import { ErrorName } from 'src/constants';
import { AuthenticatedUserLoginResponseDto, ErrorResponseDto } from 'src/dtos';
import { useResendMfaCodeCountdown } from 'src/hooks';
import { MInput, MButton, CardLayout, RequestErrorMessage, MAlert, MTypography } from 'src/lib';
import { BaseStyle, Color, Spacing } from 'src/styles';
import {
  renderTradingBlockSamePasswordErrorMessage,
  renderTradingBlockPasswordExpirationErrorMessage,
  renderTooManyFailedLoginAttemptsErrorMessage,
  formatSecondsCountdown,
} from 'src/utils';
import * as Yup from 'yup';

import * as Styles from '../BaseLayout.styles';

export const loginMFAValidationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Code is required.')
    .min(6, 'Confirmation code should contain 6 numbers')
    .max(6, 'Confirmation code should contain 6 numbers'),
});

export interface MfaConfirmFormValues {
  code: string;
}

export const LoginMfa = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorResponseDto>();

  const email = useSelector((state: any) => state.auth.data?.email);
  const password = useSelector((state: any) => state.auth.data?.password);
  const phoneNumber = useSelector((state: any) => state.auth.data?.phoneNumber);

  const confirmMfaError: ErrorResponseDto | undefined = useSelector((state: any) => state.user.confirmMfaCode.error);
  const isConfirmMfaCodeLoading = useSelector((state: any) => Boolean(state.user.confirmMfaCode.__requested));
  const confirmMfaCodeSucceeded = useSelector((state: any) => Boolean(state.user.confirmMfaCode.__succeeded));
  const confirmMfaData: AuthenticatedUserLoginResponseDto | undefined = useSelector(
    (state: any) => state.user.confirmMfaCode.data,
  );
  const resendMfaError: ErrorResponseDto | undefined = useSelector((state: any) => state.user.resendMfaCode.error);
  const isResendMfaCodeLoading = useSelector((state: any) => Boolean(state.user.resendMfaCode.__requested));
  const { countdown, setCountdown } = useResendMfaCodeCountdown();

  const onResetPasswordRequest = () => {
    navigate('/forgot-password');
  };

  const hasExceededLoginAttempts = () =>
    includes([ErrorName.TooManyFailedLoginAttemptsError, ErrorName.TooManyLoginAttemptsError], error?.name);

  const renderError = () => {
    if (error?.error === ErrorName.TradingBlockSamePasswordError) {
      return renderTradingBlockSamePasswordErrorMessage({ onClick: onResetPasswordRequest });
    }

    if (error?.error === ErrorName.TradingBlockPasswordExpirationError) {
      return renderTradingBlockPasswordExpirationErrorMessage({ onClick: onResetPasswordRequest });
    }

    if (error?.name === ErrorName.TooManyFailedLoginAttemptsError) {
      return renderTooManyFailedLoginAttemptsErrorMessage();
    }

    return <RequestErrorMessage message={Array.isArray(error?.message) ? error?.message.join('; ') : error?.message} />;
  };

  useEffect(() => {
    if (confirmMfaCodeSucceeded && !isEmpty(confirmMfaData)) {
      window.gtag('event', 'login', {
        login_method: 'mfa',
        user_properties: {
          user_id: confirmMfaData.user.id,
          user_email: confirmMfaData.user.email,
          user_first_name: confirmMfaData.user.firstName,
          user_last_name: confirmMfaData.user.lastName,
        },
      });
      const { accessToken, idToken, tbToken } = confirmMfaData;
      const authToken = `Bearer ${accessToken}; idToken ${idToken}; tbToken ${tbToken}`;
      dispatch(setAuthentication({ authToken, tbToken }));

      navigate('/');

      return;
    }
  }, [confirmMfaCodeSucceeded]);

  useEffect(() => {
    setError(confirmMfaError);

    if (confirmMfaError?.name === ErrorName.TooManyFailedLoginAttemptsError) {
      setCountdown(0);
    }
  }, [confirmMfaError]);

  useEffect(() => {
    setError(resendMfaError);

    if (resendMfaError) {
      setCountdown(0);
    }
  }, [resendMfaError]);

  useEffect(() => {
    return () => {
      dispatch(doClearConfirmMfaCode());
      dispatch(doClearResendMfaCode());
    };
  }, []);

  return (
    <CardLayout
      cardTitle='Two-factor authentication'
      subtitle='In order to Invest in an Offer, you will first need to “Sign Up” or “Log In” to My IPO'>
      <Formik<MfaConfirmFormValues>
        initialValues={{
          code: '',
        }}
        validationSchema={loginMFAValidationSchema}
        validateOnChange
        onSubmit={values => {
          dispatch(confirmMfaCode(values));
          dispatch(doClearResendMfaCode());
        }}>
        {form => {
          return (
            <Form autoComplete='off' style={{ width: '100%' }}>
              {phoneNumber && (
                <Col span={24} className={Spacing.my16}>
                  <MTypography variant='subtitle1' align='center' color={Color.Gray9} className={Styles.description}>
                    We sent a six-digit security code to {`XXX - XXX - ${phoneNumber.substr(phoneNumber.length - 4)}`}.
                    Please enter the code in the below box.
                  </MTypography>
                </Col>
              )}
              <Col span={24} className={Spacing.my16}>
                <MInput
                  placeholder='Enter code'
                  value={form.values.code}
                  onChange={value => form.setFieldValue('code', value)}
                  error={form.errors.code}
                  disabled={hasExceededLoginAttempts()}
                  name='login-mfa-code-name-attribute'
                  autoComplete='off'
                />
              </Col>
              <Col span={24} className={Styles.resendContainer}>
                {countdown > 0 ? (
                  <MTypography variant='body1' color={Color.Gray7}>
                    You will be able to resend the verification code in {formatSecondsCountdown(countdown)}
                  </MTypography>
                ) : (
                  <Button
                    className={Styles.resendCodeButton}
                    type='link'
                    disabled={isResendMfaCodeLoading || isConfirmMfaCodeLoading || hasExceededLoginAttempts()}
                    loading={isResendMfaCodeLoading}
                    onClick={() => {
                      dispatch(resendMfaCode({ email, password }));
                      dispatch(doClearConfirmMfaCode());
                      form.resetForm();
                    }}>
                    Resend Code
                  </Button>
                )}
              </Col>
              {error && (
                <Col span={24} className={Spacing.my16}>
                  <MAlert type='error' description={renderError()} showIcon closable={false} />
                </Col>
              )}
              <Space size={8} direction='horizontal' align='center' className={cx(Spacing.my16, BaseStyle.level)}>
                <MButton
                  type='tertiary'
                  onClick={() => {
                    dispatch(logout());
                    navigate('/login');
                  }}>
                  Back
                </MButton>
                <MButton
                  type='secondary'
                  disabled={
                    isConfirmMfaCodeLoading || isResendMfaCodeLoading || !form.isValid || hasExceededLoginAttempts()
                  }
                  loading={isConfirmMfaCodeLoading}
                  onClick={() => {
                    form.submitForm();
                  }}>
                  Verify
                </MButton>
              </Space>
            </Form>
          );
        }}
      </Formik>
    </CardLayout>
  );
};
